import React from "react";
import ElakaySwiperBlock from "../../../dynamicSwipers/ElakaySwiperBlock";
import { FileText as FileTextIcon, Download, Star } from "lucide-react";
import { elakayAutoText } from "../../dataElakay";
import { getIcon as getRegistryIcon } from "../../iconKit/iconRegistry";
import { resolveHowWeDoItMediaUrl } from "../howWeDoItMediaUrl";
import { parseVideoUrl, isValidVideoRef, buildVideoEmbedUrl, buildVideoWatchUrl } from "../howWeDoItVideoUrl";
import "./blockRenderers.css";

// Pure presentational renderers — no admin/editing affordances, no network
// calls. Imported by BOTH the editor's live-preview pane and the public
// detail page (howWeDoItDetail.jsx) so the two can never structurally
// diverge: whatever is visible in the dashboard preview is exactly what the
// public page renders.

// Kept as named exports for existing importers; the parsing itself lives in
// howWeDoItVideoUrl.js (watch?...&v=, shorts, live, youtu.be, Vimeo
// showcase/unlisted/player links...).
export { extractYouTubeId, extractVimeoId } from "../howWeDoItVideoUrl";

const ASPECT_RATIO_PADDING = {
  "16:9": "56.25%",
  "4:3": "75%",
  "1:1": "100%",
  "9:16": "177.78%",
};

const formatFileSize = (bytes = 0) => {
  if (!bytes) return "";
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const TitleRenderer = ({ content = {}, settings = {} }) => {
  const level = [1, 2, 3].includes(settings.level) ? settings.level : 1;
  const Tag = `h${level}`;
  return (
    // data-elakay-field="text" -- Console/Edit Page's HTML<->Builder sync
    // (howWeDoItPageDocument.js) reads this specific marked element's text
    // back into content.text for this block; nothing else in the markup is
    // parsed for structure, only this whitelisted field marker.
    <Tag className={`hwdi-block hwdi-title hwdi-align-${settings.align || "left"}`} data-elakay-field="text">
      {content.text}
    </Tag>
  );
};

const SubtitleRenderer = ({ content = {}, settings = {} }) => (
  <p className={`hwdi-block hwdi-subtitle hwdi-align-${settings.align || "left"}`} data-elakay-field="text">{content.text}</p>
);

const TextRenderer = ({ content = {} }) => (
  <div className="hwdi-block hwdi-text" data-elakay-field="html" dangerouslySetInnerHTML={{ __html: content.html || "" }} />
);

const ImageRenderer = ({ content = {}, settings = {} }) => {
  if (!content.url) return null;
  const renderedUrl = resolveHowWeDoItMediaUrl(content.url);
  const img = (
    <img
      data-elakay-field="url"
      data-elakay-source-url={content.url}
      data-elakay-rendered-url={renderedUrl}
      src={renderedUrl}
      alt={content.alt || ""}
      title={content.title || undefined}
      style={{
        width: settings.width || "auto",
        height: settings.height || "auto",
        borderRadius: settings.borderRadius ? `${settings.borderRadius}px` : undefined,
        boxShadow: settings.shadow ? "0 10px 30px rgba(15,35,28,0.18)" : undefined,
      }}
    />
  );
  return (
    <figure className={`hwdi-block hwdi-image hwdi-align-${settings.align || "center"}`}>
      {settings.linkUrl ? (
        <a data-elakay-field="image-link" href={settings.linkUrl} target="_blank" rel="noreferrer">
          {img}
        </a>
      ) : (
        img
      )}
      {content.caption && <figcaption data-elakay-field="caption">{content.caption}</figcaption>}
    </figure>
  );
};

const VideoUnavailable = ({ content = {} }) => {
  const original = /^https?:\/\//i.test(String(content.url || "")) ? content.url : "";
  return (
    <div className="hwdi-media-error" data-elakay-field="url" data-elakay-video-source={content.source || ""} data-elakay-source-url={content.url}>
      {elakayAutoText("howWeDoIt_video_unavailable", "Cette video est indisponible (adresse non reconnue).")}
      {original && (
        <>
          {" "}
          <a href={original} target="_blank" rel="noopener noreferrer">{elakayAutoText("howWeDoIt_video_openOriginal", "Ouvrir le lien")}</a>
        </>
      )}
    </div>
  );
};

const VideoRenderer = ({ content = {}, settings = {} }) => {
  if (!content.url) return null;
  const padding = ASPECT_RATIO_PADDING[settings.aspectRatio] || ASPECT_RATIO_PADDING["16:9"];

  let inner = null;
  if (content.source === "youtube" || content.source === "vimeo") {
    // The provider is taken from the URL itself (a YouTube link saved with
    // the "vimeo" radio still plays); only validated ids reach the player.
    const ref = parseVideoUrl(content.url);
    if (!isValidVideoRef(ref)) {
      return (
        <div className="hwdi-block hwdi-video hwdi-video--unavailable" data-elakay-video-source={content.source}>
          <VideoUnavailable content={content} />
          {content.title && <div className="hwdi-video-title" data-elakay-field="title">{content.title}</div>}
        </div>
      );
    }
    const renderedUrl = buildVideoEmbedUrl(ref, {
      autoplay: !!settings.autoplay,
      muted: !!settings.muted,
      controls: settings.controls !== false,
    });
    inner = (
      <iframe
        data-elakay-field="url"
        data-elakay-video-source={content.source}
        data-elakay-source-url={content.url}
        data-elakay-rendered-url={renderedUrl}
        data-elakay-watch-url={buildVideoWatchUrl(ref)}
        src={renderedUrl}
        title={content.title || elakayAutoText("h_ffbaf58f123162", "video")}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
        loading="lazy"
      />
    );
  } else {
    const renderedUrl = resolveHowWeDoItMediaUrl(content.url);
    inner = (
      <video
        data-elakay-field="url"
        data-elakay-video-source={content.source || "upload"}
        data-elakay-source-url={content.url}
        data-elakay-rendered-url={renderedUrl}
        src={renderedUrl}
        poster={resolveHowWeDoItMediaUrl(content.thumbnail) || undefined}
        controls={settings.controls !== false}
        autoPlay={!!settings.autoplay}
        muted={!!settings.muted || !!settings.autoplay}
        playsInline
        preload="metadata"
      />
    );
  }

  return (
    <div className="hwdi-block hwdi-video" data-elakay-video-source={content.source || "upload"}>
      <div className="hwdi-video-frame" style={{ paddingBottom: padding }}>
        {inner}
      </div>
      {content.title && <div className="hwdi-video-title" data-elakay-field="title">{content.title}</div>}
      {content.description && <div className="hwdi-video-description" data-elakay-field="description">{content.description}</div>}
    </div>
  );
};

const GalleryRenderer = ({ content = {}, settings = {} }) => (
  <div
    className={`hwdi-block hwdi-gallery hwdi-align-${settings.align || "center"}`}
    style={{ gridTemplateColumns: `repeat(${settings.columns || 3}, 1fr)` }}
  >
    {(content.items || []).map((item, index) => (
      <figure key={index}>
        <img src={resolveHowWeDoItMediaUrl(item.url)} alt={item.alt || ""} />
        {item.caption && <figcaption>{item.caption}</figcaption>}
      </figure>
    ))}
  </div>
);

const ButtonRenderer = ({ content = {}, settings = {} }) => {
  if (!content.label) return null;
  return (
    <div className={`hwdi-block hwdi-button-wrap hwdi-align-${settings.align || "left"}`}>
      <a
        data-elakay-field="button"
        href={content.url || "#"}
        className={`hwdi-btn hwdi-btn--${settings.style || "primary"}`}
        target={settings.openInNewTab ? "_blank" : undefined}
        rel={settings.openInNewTab ? "noreferrer" : undefined}
      >
        {content.label}
      </a>
    </div>
  );
};

const LinkRenderer = ({ content = {}, settings = {} }) => {
  if (!content.url) return null;
  return (
    <a
      className="hwdi-block hwdi-link"
      href={content.url}
      target={settings.openInNewTab ? "_blank" : undefined}
      rel={settings.openInNewTab ? "noreferrer" : undefined}
    >
      <span className="hwdi-link-label">{content.label || content.url}</span>
      {content.description && <span className="hwdi-link-description">{content.description}</span>}
    </a>
  );
};

const TableRenderer = ({ content = {}, settings = {} }) => (
  <div className="hwdi-block hwdi-table-wrap">
    <table
      className={`hwdi-table ${settings.striped ? "hwdi-table--striped" : ""} ${
        settings.bordered ? "hwdi-table--bordered" : ""
      }`}
    >
      <thead>
        <tr>
          {(content.headers || []).map((header, index) => (
            <th key={index}>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {(content.rows || []).map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <td key={cellIndex}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const StepsRenderer = ({ content = {}, settings = {} }) => {
  const Tag = settings.numbered === false ? "ul" : "ol";
  return (
    <Tag className="hwdi-block hwdi-steps">
      {(content.items || []).map((item, index) => (
        <li key={index}>
          <div className="hwdi-step-title">{item.title}</div>
          {item.description && <div className="hwdi-step-description">{item.description}</div>}
          {item.image && <img className="hwdi-step-image" src={item.image} alt="" />}
        </li>
      ))}
    </Tag>
  );
};

const QuoteRenderer = ({ content = {}, settings = {} }) => (
  <blockquote className={`hwdi-block hwdi-quote hwdi-quote--${settings.style || "default"}`}>
    <p>{content.text}</p>
    {content.author && <cite>{content.author}</cite>}
  </blockquote>
);

const AlertRenderer = ({ content = {}, settings = {} }) => (
  <div className={`hwdi-block hwdi-alert hwdi-alert--${settings.variant || "info"}`}>{content.text}</div>
);

const CodeRenderer = ({ content = {}, settings = {} }) => (
  <pre className={`hwdi-block hwdi-code ${settings.showLineNumbers ? "hwdi-code--numbered" : ""}`}>
    <code
      className={`language-${content.language || "text"}`}
      data-elakay-field="code"
      data-elakay-language={content.language || "text"}
    >
      {content.code}
    </code>
  </pre>
);

const DividerRenderer = ({ settings = {} }) => (
  <hr
    className={`hwdi-block hwdi-divider hwdi-divider--${settings.style || "solid"} hwdi-divider--${
      settings.spacing || "md"
    }`}
  />
);

const FileRenderer = ({ content = {}, settings = {} }) => {
  if (!content.url) return null;
  return (
    <a className="hwdi-block hwdi-file" href={resolveHowWeDoItMediaUrl(content.url)} target="_blank" rel="noreferrer" download>
      <FileTextIcon size={18} />
      <span className="hwdi-file-name">{content.filename || elakayAutoText("h_f0fa5745ccc974", "Fichier")}</span>
      {settings.showFileSize && content.size ? (
        <span className="hwdi-file-size">{formatFileSize(content.size)}</span>
      ) : null}
      <Download size={16} />
    </a>
  );
};

const FaqRenderer = ({ content = {}, settings = {} }) => (
  <div className="hwdi-block hwdi-faq">
    {(content.items || []).map((item, index) => (
      <details key={index} open={settings.defaultOpenFirst && index === 0}>
        <summary>{item.question}</summary>
        <div className="hwdi-faq-answer" dangerouslySetInnerHTML={{ __html: item.answer || "" }} />
      </details>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Elementary elements imported from Page Builder. Form-field types render as
// disabled/read-only styled mockups (no form-submission backend exists here) —
// they illustrate what a field looks like, never collect real input.
// ---------------------------------------------------------------------------

const SpacerRenderer = ({ settings = {} }) => (
  <div className="hwdi-block hwdi-spacer" style={{ height: settings.height || "40px" }} />
);

const InputRenderer = ({ content = {} }) => (
  <label className="hwdi-block hwdi-field-mock">
    {content.label && <span className="hwdi-field-mock-label">{content.label}</span>}
    <input type="text" placeholder={content.placeholder || ""} disabled />
  </label>
);

const TextareaRenderer = ({ content = {}, settings = {} }) => (
  <label className="hwdi-block hwdi-field-mock">
    {content.label && <span className="hwdi-field-mock-label">{content.label}</span>}
    <textarea placeholder={content.placeholder || ""} rows={settings.rows || 4} disabled />
  </label>
);

const SelectRenderer = ({ content = {} }) => (
  <label className="hwdi-block hwdi-field-mock">
    {content.label && <span className="hwdi-field-mock-label">{content.label}</span>}
    <select disabled>
      {(content.choices || []).map((choice, index) => (
        <option key={index}>{choice}</option>
      ))}
    </select>
  </label>
);

const CheckboxRenderer = ({ content = {}, settings = {} }) => (
  <label className="hwdi-block hwdi-field-mock hwdi-field-mock--inline">
    <input type="checkbox" checked={!!settings.checked} disabled readOnly />
    <span>{content.label}</span>
  </label>
);

const DatefieldRenderer = ({ content = {} }) => (
  <label className="hwdi-block hwdi-field-mock">
    {content.label && <span className="hwdi-field-mock-label">{content.label}</span>}
    <input type="date" disabled />
  </label>
);

const RadioRenderer = ({ content = {}, settings = {} }) => (
  <label className="hwdi-block hwdi-field-mock hwdi-field-mock--inline">
    <input type="radio" name={content.name} checked={!!settings.checked} disabled readOnly />
    <span>{content.label}</span>
  </label>
);

const ToggleRenderer = ({ content = {}, settings = {} }) => (
  <label className="hwdi-block hwdi-field-mock hwdi-field-mock--inline">
    <span className={`hwdi-toggle-mock ${settings.checked ? "hwdi-toggle-mock--on" : ""}`}>
      <span className="hwdi-toggle-mock-knob" />
    </span>
    <span>{content.label}</span>
  </label>
);

const OutputRenderer = ({ content = {} }) => (
  <span className="hwdi-block hwdi-output">{content.text}</span>
);

const DatalistRenderer = ({ content = {} }) => (
  <label className="hwdi-block hwdi-field-mock">
    {content.label && <span className="hwdi-field-mock-label">{content.label}</span>}
    <input type="text" placeholder={content.placeholder || ""} disabled />
    {(content.choices || []).length > 0 && (
      <span className="hwdi-field-mock-hint">{(content.choices || []).join(" · ")}</span>
    )}
  </label>
);

// content.iconId (registre iconKit, vrai SVG) prime sur content.glyph
// (symbole personnalisé) -- même priorité que PageBuilder.jsx, seul le
// contexte de rendu diffère (email = glyphe seul, cette page = vrai SVG,
// rendue en React aussi bien ici que sur la page publique).
const IconRenderer = ({ content = {}, settings = {} }) => {
  const icon = getRegistryIcon(content.iconId);
  const size = settings.size || "48px";
  return (
    <div className="hwdi-block hwdi-icon-mock" style={{ fontSize: size }}>
      {icon ? <icon.Component size={parseInt(size, 10) || 48} /> : (content.glyph || "★")}
    </div>
  );
};

const StatRenderer = ({ content = {} }) => (
  <div className="hwdi-block hwdi-stat">
    <strong>{content.value}</strong>
    {content.label && <span>{content.label}</span>}
  </div>
);

const ProgressRenderer = ({ content = {} }) => {
  const percent = Math.max(0, Math.min(100, Number(content.percent) || 0));
  return (
    <div className="hwdi-block hwdi-progress">
      {content.label && <div className="hwdi-progress-label">{content.label} — {percent}%</div>}
      <div className="hwdi-progress-track">
        <div className="hwdi-progress-bar" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};

const RatingRenderer = ({ content = {} }) => {
  const max = Number(content.max) || 5;
  const value = Number(content.value) || 0;
  return (
    <div className="hwdi-block hwdi-rating">
      {Array.from({ length: max }, (_, index) => (
        <Star key={index} size={18} className={index < value ? "hwdi-rating-star--on" : "hwdi-rating-star"} />
      ))}
      {content.label && <span className="hwdi-rating-label">{content.label}</span>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Layout/container elements imported from Page Builder. Unlike every other
// renderer, these receive their nested children as React `children` and
// render them INSIDE their own styled box (not as a sibling section below,
// which is how ordinary blocks display their nested children) — see
// `renderBlock`'s CONTAINER_TYPES branch.
// ---------------------------------------------------------------------------

const ContainerEmptyState = () => (
  <div className="hwdi-container-empty">
    {elakayAutoText("howWeDoIt_container_empty", "Bloc vide — ajoutez des blocs imbriques.")}
  </div>
);

const ContainerRenderer = ({ children }) => (
  <div className="hwdi-block hwdi-container">
    {React.Children.count(children) > 0 ? children : <ContainerEmptyState />}
  </div>
);

const RowRenderer = ({ settings = {}, children }) => (
  <div className="hwdi-block hwdi-row" style={{ gridTemplateColumns: `repeat(${settings.columns || 2}, 1fr)` }}>
    {React.Children.count(children) > 0 ? children : <ContainerEmptyState />}
  </div>
);

const StackRenderer = ({ children }) => (
  <div className="hwdi-block hwdi-stack">
    {React.Children.count(children) > 0 ? children : <ContainerEmptyState />}
  </div>
);

const SectionRenderer = ({ children }) => (
  <div className="hwdi-block hwdi-section">
    {React.Children.count(children) > 0 ? children : <ContainerEmptyState />}
  </div>
);

const PanelRenderer = ({ children }) => (
  <div className="hwdi-block hwdi-panel">
    {React.Children.count(children) > 0 ? children : <ContainerEmptyState />}
  </div>
);

// Swiper Manager swiper (by reference) -- the one renderer, <DynamicSwiper>.
const ElakaySwiperRenderer = ({ content = {}, settings = {} }) => (
  <div className="hwdi-block hwdi-elakay-swiper">
    <ElakaySwiperBlock swiperId={content.swiperId} swiperName={content.swiperName} maxWidth={Number(settings.maxWidth) || 0} />
  </div>
);

const CONTAINER_TYPES = new Set(["container", "row", "stack", "section", "panel"]);

const RENDERERS = {
  title: TitleRenderer,
  subtitle: SubtitleRenderer,
  text: TextRenderer,
  image: ImageRenderer,
  video: VideoRenderer,
  gallery: GalleryRenderer,
  button: ButtonRenderer,
  link: LinkRenderer,
  table: TableRenderer,
  steps: StepsRenderer,
  quote: QuoteRenderer,
  alert: AlertRenderer,
  code: CodeRenderer,
  divider: DividerRenderer,
  file: FileRenderer,
  faq: FaqRenderer,
  spacer: SpacerRenderer,
  input: InputRenderer,
  textarea: TextareaRenderer,
  select: SelectRenderer,
  checkbox: CheckboxRenderer,
  datefield: DatefieldRenderer,
  radio: RadioRenderer,
  toggle: ToggleRenderer,
  output: OutputRenderer,
  datalist: DatalistRenderer,
  icon: IconRenderer,
  stat: StatRenderer,
  progress: ProgressRenderer,
  rating: RatingRenderer,
  elakaySwiper: ElakaySwiperRenderer,
  container: ContainerRenderer,
  row: RowRenderer,
  stack: StackRenderer,
  section: SectionRenderer,
  panel: PanelRenderer,
};

export const renderBlock = (block) => {
  const Renderer = RENDERERS[block.type];
  if (!Renderer) return null;
  const children = (block.children || []).filter((child) => child.visible !== false);
  // Position / size / layer / spacing (settings.layout) are not inline: the
  // frame adds one stylesheet for all blocks and all devices
  // (buildLayoutCss in blockLayout.js), keyed on data-elakay-block-id.

  // data-elakay-block-id/-type: stable markers read back by
  // howWeDoItPageDocument.js's deserializer (block order/add/remove
  // reconciliation) -- one single place, so every block type and both the
  // top-level and nested-children cases get them automatically.
  if (CONTAINER_TYPES.has(block.type)) {
    return (
      <div
        key={block.blockId}
        id={`hwdi-block-${block.blockId}`}
        className="hwdi-block-wrap"
        data-elakay-block-id={block.blockId}
        data-elakay-block-type={block.type}
      >
        <Renderer content={block.content || {}} settings={block.settings || {}}>
          {children.map((child) => renderBlock(child))}
        </Renderer>
      </div>
    );
  }

  return (
    <div
      key={block.blockId}
      id={`hwdi-block-${block.blockId}`}
      className="hwdi-block-wrap"
      data-elakay-block-id={block.blockId}
      data-elakay-block-type={block.type}
    >
      <Renderer content={block.content || {}} settings={block.settings || {}} />
      {children.length > 0 && (
        <div className="hwdi-block-children">{children.map((child) => renderBlock(child))}</div>
      )}
    </div>
  );
};

const BlockList = ({ blocks = [] }) => (
  <div className="hwdi-block-list">
    {[...blocks]
      .filter((block) => block.visible !== false)
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((block) => renderBlock(block))}
  </div>
);

export default BlockList;
